import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { setToken } from "@/lib/api";
import { makeT } from "@/lib/terminology";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [salon, setSalon] = useState(null);
  const [plan, setPlan] = useState(null);
  const [features, setFeatures] = useState([]);
  const [industry, setIndustry] = useState(null);
  const [terminology, setTerminology] = useState({});
  const [trial, setTrial] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem("studiomv_token")) {
      setUser(false); setSalon(null); setPlan(null); setFeatures([]);
      setIndustry(null); setTerminology({}); setTrial(null); setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setSalon(data.salon);
      setPlan(data.plan || null);
      setFeatures(Array.isArray(data.features) ? data.features : []);
      setIndustry(data.industry || null);
      setTerminology(data.terminology || {});
      setTrial(data.trial || null);
    } catch {
      setToken(null);
      setUser(false);
      setSalon(null);
      setPlan(null);
      setFeatures([]);
      setIndustry(null);
      setTerminology({});
      setTrial(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    await refresh();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setToken(null);
    setUser(false);
    setSalon(null);
    setPlan(null);
    setFeatures([]);
    setIndustry(null);
    setTerminology({});
    setTrial(null);
  };

  const hasFeature = (key) =>
    user && user.role === "super_admin" ? true : features.includes(key);

  const t = useMemo(() => makeT(terminology), [terminology]);

  return (
    <AuthContext.Provider value={{
      user, salon, plan, features, hasFeature, loading,
      industry, terminology, t, trial,
      login, logout, refresh,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
